/**
 * Editorial content for /api.
 *
 * The samples are the page's real argument, so they are stored as plain source
 * and coloured at render time rather than kept as pre-tokenised fragments —
 * that way a developer reading the file sees the same thing a visitor does, and
 * the snippets stay copy-pasteable.
 */
import {
  API_DAILY_LIMIT_PREMIUM_DISPLAY,
  PLUS_MONTHLY_CREDITS_DISPLAY,
  PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY,
} from './config';
import { MODEL_TOTAL, PROVIDER_TOTAL } from './modelInventory.generated';

export { MODALITY_COUNTS, MODEL_TOTAL, PROVIDER_TOTAL } from './modelInventory.generated';

export const BASE_URL = 'https://gab.ai/v1';

export const HERO_MARQUEE = [
  `${MODEL_TOTAL} models`,
  'Frontier + open source',
  'Auto Router',
  'OpenAI-compatible',
  `${PROVIDER_TOTAL} providers`,
  'One API key',
  'Streaming',
  'Text · Image · Video · Audio',
  `Up to ${API_DAILY_LIMIT_PREMIUM_DISPLAY} requests/day`,
];

/**
 * The two-path sell, expressed as the string a developer actually types. The
 * hero states it this way because the choice *is* one field.
 */
export const HERO_PATHS = [
  {
    value: 'model: "gpt-5-5"',
    label: 'You pick',
    copy: `Pin any of ${MODEL_TOTAL} frontier and open-source models by id or alias. One key, one SDK, ${PROVIDER_TOTAL} providers.`,
  },
  {
    value: 'model: "auto"',
    label: 'We pick',
    accent: true,
    copy: 'Auto Router classifies the turn, grades coding as quick, standard, or hard, then holds that model for the rest of a tool loop.',
  },
];

/** The response headers Auto Router sets. Shown rather than described. */
export const AUTO_TRACE = [
  ['x-gab-router-intent', 'coding'],
  ['x-gab-router-coding-tier', 'standard'],
  ['x-gab-selected-model', 'glm-5-3'],
  ['x-gab-routed-model', 'glm-5-3'],
];

/**
 * Quickstart tabs. `language` selects the highlighter's keyword set; `endpoint`
 * labels the route each sample exercises.
 */
export const QUICKSTART = [
  {
    id: 'python',
    label: 'Python',
    language: 'python',
    endpoint: '/v1/chat/completions',
    code: `from openai import OpenAI

client = OpenAI(
    api_key="YOUR_GAB_API_KEY",
    base_url="${BASE_URL}",
)

response = client.chat.completions.create(
    model="auto",
    messages=[
        {"role": "user", "content": "Hello, how are you?"}
    ],
)

print(response.choices[0].message.content)`,
  },
  {
    id: 'node',
    label: 'Node.js',
    language: 'javascript',
    endpoint: '/v1/chat/completions',
    code: `import OpenAI from 'openai';

const client = new OpenAI({
  apiKey: process.env.GAB_API_KEY,
  baseURL: '${BASE_URL}',
});

const response = await client.chat.completions.create({
  model: 'auto',
  messages: [
    { role: 'user', content: 'Hello, how are you?' },
  ],
});

console.log(response.choices[0].message.content);`,
  },
  {
    id: 'curl',
    label: 'cURL',
    language: 'bash',
    endpoint: '/v1/chat/completions',
    code: `curl ${BASE_URL}/chat/completions \\
  -H "Authorization: Bearer $GAB_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "auto",
    "messages": [{"role": "user", "content": "Hello, how are you?"}]
  }'`,
  },
  {
    id: 'claude',
    label: 'Claude Code',
    language: 'bash',
    endpoint: '/v1/messages',
    code: `# Claude Code speaks Anthropic's /v1/messages API.
# Point it at the root domain — the client appends /v1/messages itself.

export ANTHROPIC_BASE_URL="https://gab.ai"
export ANTHROPIC_AUTH_TOKEN="YOUR_GAB_API_KEY"
export ANTHROPIC_MODEL="auto"
export ANTHROPIC_SMALL_FAST_MODEL="arya"

claude`
  },
  {
    id: 'codex',
    label: 'Codex',
    language: 'bash',
    endpoint: '/v1/responses',
    code: `# Codex and other OpenAI-style agents use the compatible base URL.
# Newer agent clients reach for /v1/responses on tool workflows.

export OPENAI_BASE_URL="${BASE_URL}"
export OPENAI_API_KEY="YOUR_GAB_API_KEY"

codex`,
  },
  {
    id: 'hermes',
    label: 'Hermes',
    language: 'bash',
    endpoint: '/v1/chat/completions',
    code: `# Hermes works with any OpenAI-compatible endpoint.
# Run 'hermes model', choose "Custom endpoint", then supply:
#
#   API base URL: ${BASE_URL}
#   API key:      YOUR_GAB_API_KEY
#   Model name:   auto   (or any id from /v1/models)

hermes`,
  },
  {
    id: 'prime-agent',
    label: 'Prime Agent',
    language: 'bash',
    endpoint: '/v1/chat/completions',
    code: `# Prime Agent uses a custom provider in ~/.prime/agent/models.json.
# Point baseUrl at Gab AI, then launch with --provider gab-ai.

export GAB_AI_API_KEY="YOUR_GAB_API_KEY"
prime-agent --provider gab-ai --model auto`,
  },
];

/** Model routing in one line, for the "pick a model" step. */
export const MODEL_SWITCH_SNIPPET = `# One key, every model. Swap the id, keep the code.

client.chat.completions.create(model="auto", messages=…)
client.chat.completions.create(model="gpt-5-5", messages=…)
client.chat.completions.create(model="claude-opus-5", messages=…)
client.chat.completions.create(model="gemini-3-5-flash", messages=…)
client.images.generate(model="seedream-4-5", prompt="a sunset")
client.videos.generate(model="veo-3-1", prompt="a city at dusk")`;

export const AUTO_SNIPPET = `from openai import OpenAI

client = OpenAI(
    api_key="YOUR_GAB_API_KEY",
    base_url="${BASE_URL}",
)

# Auto Router classifies the kickoff (quick / standard / hard for coding)
# and sticks that model for a Cursor / Claude Code / Codex tool loop.
response = client.chat.completions.create(
    model="auto",
    messages=[
        {"role": "user", "content": "Write a debounce helper and tests"}
    ],
)

print(response.model)  # slug that actually ran
# Also inspect X-Gab-Routed-Model, X-Gab-Router-Intent, X-Gab-Router-Coding-Tier
print(response.choices[0].message.content)`;

/**
 * The routing table, as a table.
 *
 * `model` is the shipped default for that lane. Admins can repoint any lane, so
 * the section says so rather than implying the mapping is frozen.
 */
export const AUTO_ROUTES = [
  {
    prompt: 'What does map return in Python',
    intent: 'coding',
    tier: 'quick',
    model: 'deepseek-v4-flash',
  },
  {
    prompt: 'Write a debounce helper and tests',
    intent: 'coding',
    tier: 'standard',
    model: 'glm-5-3',
  },
  {
    prompt: 'Migrate this Express app to Fastify, keep sessions',
    intent: 'coding',
    tier: 'hard',
    model: 'kimi-k3',
  },
  {
    prompt: 'Who called the race last night?',
    intent: 'search-web',
    tier: '—',
    model: 'arya + live web',
  },
  {
    prompt: 'Find the hole in this proof',
    intent: 'use-reasoning',
    tier: '—',
    model: 'kimi-k3',
  },
  {
    prompt: 'What does the First Amendment protect here?',
    intent: 'political',
    tier: '—',
    model: 'arya',
  },
];

/**
 * One agent loop, turn by turn. Stickiness is the hardest part of the router to
 * describe in prose and the easiest to show as a trace.
 */
export const AUTO_LOOP = [
  {
    turn: '1',
    input: 'user · "Write a debounce helper and tests"',
    model: 'glm-5-3',
    router: 'classified',
  },
  {
    turn: '2',
    input: 'tool_result · Read(debounce.js)',
    model: 'glm-5-3',
    router: 'skipped',
  },
  {
    turn: '3',
    input: 'tool_result · Edit(debounce.test.js)',
    model: 'glm-5-3',
    router: 'skipped',
  },
  {
    turn: '4',
    input: 'user · "now write the README"',
    model: 'arya',
    router: 'reclassified',
  },
];

/** The three coding grades, and the rule that decides each one. */
export const CODING_TIERS = [
  {
    tier: 'quick',
    rule: 'Syntax, one-liners, short explanations',
    copy: 'Cheap and fast. Ambiguous coding never lands here — uncertainty resolves upward, not down.',
  },
  {
    tier: 'standard',
    rule: 'One contained unit of work',
    copy: 'Implement, test, or review a file. The default grade, and the floor for a confirmed Cursor or Claude Code session.',
  },
  {
    tier: 'hard',
    rule: 'Repo-scale, multi-file, security-sensitive',
    copy: 'Graded off the kickoff prompt. Declaring tools[] does not promote a request on its own.',
  },
];

/**
 * Every route, grouped the way a developer looks for them. The groups double as
 * the section's structure, so adding a route is a one-line change.
 */
export const ENDPOINT_GROUPS = [
  {
    group: 'Chat and agents',
    endpoints: [
      { method: 'POST', path: '/v1/chat/completions', label: 'OpenAI chat completions' },
      { method: 'POST', path: '/v1/responses', label: 'OpenAI responses' },
      { method: 'POST', path: '/v1/messages', label: 'Anthropic messages' },
      { method: 'GET', path: '/v1/models', label: 'List available models' },
      { method: 'GET', path: '/v1/models/{id}', label: 'Retrieve a model by slug or alias' },
      { method: 'GET', path: '/v1/responses/{id}', label: 'Retrieve a stored Responses result' },
      { method: 'GET', path: '/v1/agent-setup', label: 'Generated Cursor / OpenCode / OpenClaw configs' },
      { method: 'GET', path: '/openapi.json', label: 'OpenAPI spec' },
      { method: 'GET', path: '/llms-full.txt', label: 'Generated model catalog for agents' },
    ],
  },
  {
    group: 'Generation',
    endpoints: [
      { method: 'POST', path: '/v1/images/generations', label: 'Image generation' },
      { method: 'POST', path: '/v1/images/edit', label: 'Image editing (reference images)' },
      { method: 'POST', path: '/v1/videos/generations', label: 'Video generation' },
      { method: 'POST', path: '/v1/audio/speech', label: 'Text to speech' },
      { method: 'POST', path: '/v1/embeddings', label: 'Embeddings' },
    ],
  },
  {
    group: 'Files and account',
    endpoints: [
      { method: 'POST', path: '/v1/files', label: 'Upload files for reuse' },
      { method: 'GET', path: '/v1/credits', label: 'Credit balance' },
      { method: 'GET', path: '/v1/api-keys', label: 'API key management' },
      { method: 'GET', path: '/v1/account/export', label: 'Full account export' },
    ],
  },
];

/** Total routes, quoted by the hero strip and the reference section head. */
export const ROUTE_TOTAL = ENDPOINT_GROUPS.reduce(
  (total, group) => total + group.endpoints.length,
  0,
);

/**
 * Operational surface, as a spec list. `spec` is the concrete artefact — a
 * route, a header, an env var — so each row can be verified rather than trusted.
 */
export const CAPABILITIES = [
  {
    title: `${MODEL_TOTAL} models, one API`,
    spec: 'GET /v1/models',
    copy: `Swap GPT, Claude, Gemini, Grok, Llama, DeepSeek, Arya, and every other live model by changing a string. Frontier and open-source, ${PROVIDER_TOTAL} providers, same key.`,
  },
  {
    title: 'Auto Router',
    spec: 'model="auto"',
    copy: 'Coding is graded quick, standard, or hard, and the kickoff model holds for a Cursor, Claude Code, or Codex tool loop. Lane mapping can change; you are billed for the model that actually ran.',
  },
  {
    title: 'Built for agents',
    spec: 'GET /v1/agent-setup',
    copy: 'Function calling, streaming tool-call deltas, and generated configs for the CLIs and IDEs you already run.',
  },
  {
    title: 'Drop-in compatible',
    spec: 'base_url',
    copy: 'Point the OpenAI SDK you already use at our base URL and stop. Anthropic clients work on the same key, and aliases like gpt-5.5 or anthropic/claude-sonnet-4-5 resolve automatically.',
  },
  {
    title: 'Vision and file input',
    spec: 'POST /v1/files',
    copy: 'Send images by URL, base64, or file_id. Upload once and reference the same file across as many requests as you like.',
  },
  {
    title: `Up to ${API_DAILY_LIMIT_PREMIUM_DISPLAY} requests a day`,
    spec: 'X-RateLimit-Remaining',
    copy: 'Plus includes 10,000 requests/day and Plus Premium includes 50,000, with budget headers on every response.',
  },
  {
    title: 'Streaming by default',
    spec: 'stream=true',
    copy: 'Server-sent events on every chat route, including incremental tool-call argument deltas, so an agent can start Read or Edit before the turn finishes.',
  },
  {
    title: 'Portable account data',
    spec: 'GET /v1/account/export',
    copy: 'Chats, memories, files, agents, and collections — JSON for scripting, or ?format=zip for the same bundle the app produces.',
  },
  {
    title: 'Usage you can audit',
    spec: 'GET /v1/usage',
    copy: 'Every request with the model that ran, credits, tokens, and the Auto Router intent and coding tier when you passed model="auto".',
  },
];

/**
 * Coding agents, CLIs, and IDEs the API sits behind. `setup` is the single line
 * that does the work, which is the part a developer is scanning for.
 */
export const AGENT_INTEGRATIONS = [
  {
    name: 'Cursor',
    kind: 'IDE',
    protocol: 'OpenAI + Anthropic',
    setup: '/v1/agent-setup?client=cursor',
    href: '/docs/cursor-ide',
    copy: 'Agent and Composer run on auto. Pin a concrete slug for Tab and inline complete — those must not route.',
  },
  {
    name: 'Claude Code',
    kind: 'CLI',
    protocol: 'Anthropic',
    setup: 'ANTHROPIC_MODEL=auto',
    href: '/docs/claude-code',
    copy: 'Base URL is the root domain — the client appends /v1/messages. Keep a cheap explicit slug for compact and title calls.',
  },
  {
    name: 'Codex',
    kind: 'CLI',
    protocol: 'OpenAI',
    setup: 'OPENAI_BASE_URL=https://gab.ai/v1',
    href: '/docs/codex-cli',
    copy: 'Responses and chat completions both route. The kickoff model is held for the whole tool loop.',
  },
  {
    name: 'OpenCode',
    kind: 'CLI',
    protocol: 'OpenAI Responses',
    setup: '/v1/agent-setup?client=opencode',
    href: '/docs/opencode',
    copy: 'Use @ai-sdk/openai so tool turns hit /v1/responses. Includes context limits and tool_call flags so compaction behaves.',
  },
];

export const AGENT_INTEGRATION_MORE = [
  { name: 'OpenClaw', href: '/docs/openclaw' },
  { name: 'Hermes', href: '/docs/hermes' },
  { name: 'Prime Agent', href: '/docs/prime-agent' },
  { name: 'Pi CLI', href: '/docs/pi-cli' },
];

export const ACCESS_NOTES = [
  'Gab AI Plus or Plus Premium is required to issue an API key',
  'Every request draws from your credit balance, with a one-credit floor',
  `Plus includes ${PLUS_MONTHLY_CREDITS_DISPLAY} credits a month; Premium includes ${PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY}`,
  `Swap any of ${MODEL_TOTAL} models by id, or pass model="auto" — coding tiers plus a sticky harness model`,
];

export const CLOSING_MARKS = ['Made in USA', 'OpenAI-compatible', 'Founded 2016'];
